import { useEffect, useState } from "react";
import http from "@/api";
import { TIER_META, TIER_BADGE_IMG } from "@/constants";
import Mariposa from "@/components/Mariposa";
import GemBadge from "@/components/GemBadge";
import { Download, Share2, Copy, Check, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DigitalSeal({ badge }) {
  const [certTiers, setCertTiers] = useState([]);
  useEffect(() => {
    http.get("/certificates/available").then((r) => setCertTiers(r.data.tiers || [])).catch(() => {});
  }, []);
  const meta = TIER_META[badge.tier] || TIER_META.sin_clasificar;
  const canDownload = badge.tier && badge.tier !== "sin_clasificar";
  const publicUrl = `${window.location.origin}/sello/${badge.supplier_id}`;
  const [copied, setCopied] = useState(false);

  const downloadVia = (format) => {
    const a = document.createElement("a");
    a.href = `${API}/suppliers/${badge.supplier_id}/seal?format=${format}`;
    a.download = `sello-lala-elite-${badge.supplier_id?.slice(0, 8)}.${format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const copyPublic = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      window.prompt("Copia este enlace público:", publicUrl);
    }
  };

  return (
    <div data-testid="digital-seal" className="sketch-border bg-[var(--cream-soft)] p-6 flex flex-col items-center gap-4">
      {/* Preview card (inline preview using the live Mariposa) */}
      <div className="w-full max-w-[300px] min-h-[225px] bg-[var(--cream)] rounded-xl border border-[var(--line)] p-4 flex flex-col items-center justify-center text-center"
        style={{ borderColor: meta.color }}>
        <div className="font-hand text-sm text-[var(--lala-blue)]">distintivo Lala Elite</div>
        {TIER_BADGE_IMG[badge.tier]
          ? <img src={TIER_BADGE_IMG[badge.tier]} alt={`Medalla ${meta.label}`}
                 className="w-[64px] h-[64px] object-contain drop-shadow-sm my-1"
                 data-testid="seal-medal-img" />
          : <Mariposa size={90} animated palette="lala" />}
        <div className="font-display text-2xl text-[var(--ink)]">{meta.label}</div>
        {canDownload && <GemBadge tier={badge.tier} size="sm" variant="pill" className="mt-1" />}
        <div className="font-hand text-base" style={{ color: meta.color }}>{meta.tagline}</div>
        <div className="text-xs text-[var(--ink-soft)] mt-2 truncate w-full">{badge.supplier_name}</div>
        <div className="text-[10px] text-[var(--ink-soft)] mt-0.5">
          {badge.period && `período ${badge.period} · `}código <b className="font-mono">{badge.verify_code}</b>
        </div>
      </div>

      {canDownload ? (
        <>
          <div className="text-center">
            <div className="font-hand text-xl text-[var(--lala-blue)]">Brillas con nosotros — gracias por crecer junto a Lala.</div>
            <div className="text-xs text-[var(--ink-soft)] mt-1">Sello digital verificable · {meta.range}</div>
          </div>
          <div className="grid grid-cols-2 gap-2 w-full">
            {certTiers.includes(badge.tier) && (
              <a href={`${API}/suppliers/${badge.supplier_id}/certificate`}
                download={`certificado-lala-elite-${badge.supplier_id?.slice(0, 8)}.png`}
                data-testid="download-certificate-btn"
                className="col-span-2 sketch-pill bg-[#E4002B] text-white px-3 py-2 text-sm font-semibold inline-flex items-center justify-center gap-1.5 hover:opacity-90">
                <Download size={14} /> Certificado oficial (PNG)
              </a>
            )}
            <button data-testid="download-seal-png" onClick={() => downloadVia("png")} className="btn-primary text-sm justify-center">
              <Download size={14} /> PNG
            </button>
            <button data-testid="download-seal-svg" onClick={() => downloadVia("svg")} className="btn-ghost text-sm justify-center">
              <Download size={14} /> SVG
            </button>
            <a href={publicUrl} target="_blank" rel="noopener noreferrer"
              data-testid="view-public-seal"
              className="sketch-pill bg-[var(--cream)] text-[var(--lala-blue)] border border-[var(--lala-blue)] px-3 py-1.5 text-xs font-semibold inline-flex items-center justify-center gap-1.5">
              <ExternalLink size={12} /> Ver público
            </a>
            <button onClick={copyPublic}
              data-testid="copy-public-link"
              className="sketch-pill bg-[var(--cream)] text-[var(--lala-blue)] border border-[var(--lala-blue)] px-3 py-1.5 text-xs font-semibold inline-flex items-center justify-center gap-1.5">
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? "¡Copiado!" : "Copiar enlace"}
            </button>
          </div>
          <div className="text-[10px] text-[var(--ink-soft)] text-center mt-2 leading-relaxed">
            <Share2 size={10} className="inline mr-1" />
            Comparte el enlace público en tu sitio web, firma de email o redes sociales.
          </div>
        </>
      ) : (
        <div className="text-xs text-[var(--ink-soft)] italic text-center max-w-xs">
          Tu sello digital se desbloqueará en cuanto alcances un nivel evaluado.
        </div>
      )}
    </div>
  );
}
